<template>
  <div class="container-launch" style="flex: 1;">
    <div class="flex flex-column justify-space-between audit-wizard-inner">
      <div class="wizard-main-column">
        <div
          v-if="activeAudit && currentStep === STEPS.length + 1"
          class="onboarding-completed"
        >
          <img src="/static/whote_glove_site_content_audit_onboarding_completed.png" />
          <div>
            <div class="flex align-center" style="gap: 8px;">
              <div style="width: 16px; height: 16px; border-radius: 50%; background-color: #4CAF50; display: flex; align-items: center; justify-content: center;">
                <i class="material-icons" style="color: white; font-size: 12px;">check</i>
              </div>
              <div class="mainColor--text" style="font-weight: 800; font-size:18px; line-height: 1.5;">
                Onboarding completed
              </div>
            </div>
            <div class="mainColor--text" style="font-weight: 800; font-size: 1.4rem; line-height: 1.5;">
              Reorder your next audit 
              <div class="mt-2" style="font-weight: normal; font-size: 0.9rem; line-height: 1.5;">
                If you want to run another audit for this site, you can order a new audit whenever you're ready. We recommend auditing your content every 3-4 months as your content evolves.
              </div>
              <div class="d-flex align-center justify-space-between mt-4">
                <div>
                  <v-btn
                    v-if="showOrderNewAuditButton"
                    rounded
                    color="profileAnchorColor"
                    class="button-with-icon text-none whiteColor--text padding8 height38"
                    tag="a"
                    :loading="newAuditOrderLoading"
                    @click="newAuditOrder();"
                  >
                    Order new audit
                  </v-btn>
                </div>
                <b-dropdown
                  v-if="whiteGloveMissionControlAdminUi"
                  aria-role="list"
                >
                  <template #trigger>
                    <b-button
                      :label="getFulfillementStatusLabel(activeAudit.fulfillementStatus)"
                      icon-right="angle-down" />
                  </template>
                  <b-dropdown-item v-if="activeAudit.fulfillementStatus !== 'pending'" aria-role="listitem" @click="updateAudit({set_pending: true})">{{ getFulfillementStatusLabel('pending')}}</b-dropdown-item>
                  <b-dropdown-item v-if="activeAudit.fulfillementStatus !== 'in_progress'" aria-role="listitem" @click="updateAudit({set_in_progress: true})">{{ getFulfillementStatusLabel('in_progress')}}</b-dropdown-item>
                  <b-dropdown-item v-if="activeAudit.fulfillementStatus !== 'completed'" aria-role="listitem" @click="updateAudit({set_completed: true})">{{ getFulfillementStatusLabel('completed')}}</b-dropdown-item>
                </b-dropdown>
              </div>
            </div>
          </div>
        </div>

        <div v-else class="flex flex-column wizard-flow" style="flex: 1; position: relative;">

          <div class="steps-status mt-2 mb-6">
            <div
              v-for="(step, idx) in STEPS"
              :key="idx"
              :class="{
                'step-item': true,
                'active': currentStep === (idx + 1),
                'completed': isStepValid(idx + 1)
              }"
            >
              <div class="step-dot-label-group">
                <div class="step-dot">
                  <span class="step-number">{{ idx + 1 }}</span>
                  <i class="material-icons">check</i>
                </div>
                <div class="step-label">
                  {{ step.label }}
                </div>
              </div>
              <div class="step-seperator" v-if="idx < (STEPS.length - 1)"></div>
            </div>
          </div>

          <div>
            <v-btn
              v-if="isStepValid(1) && whiteGloveMissionControlAdminUi"
              rounded
              color="profileAnchorColor"
              class="button-with-icon text-none whiteColor--text padding8 height38 mb-6"
              href="#" @click.prevent="showForceOnboardingCompletedConfirmModal = true"
            >Set onboarding as completed</v-btn>
            <v-btn
              v-else-if="!isStepValid(1) && whiteGloveMissionControlAdminUi"
              rounded
              color="profileAnchorColor"
              class="button-with-icon text-none whiteColor--text padding8 height38 mb-6"
              href="#" @click.prevent="showSetPaidConfirmModal = true"
            >Set as paid</v-btn>
          </div>
    
          <div
            v-if="currentStep === 1"
            :class="{
              'step-content': true,
              'completed': isStepValid(currentStep),
              'show-border': !isStepValid(1)
            }"
          >
            <div class="plan-card mb-4">
              <div class="step-text step-text-title mb-2">Choose your audit plan</div>
              <div class="step-text step-text-description mb-6">
                Both options include a full site analysis and content plans with topic clusters.
              </div>
              <v-btn
                v-if="isStepValid(currentStep)"
                rounded
                color="profileAnchorColor"
                class="success-disabled-btn button-with-icon text-none padding8 height38 mb-4"
                tag="a"
                :disabled="true"
              >
                <i class="material-icons-outlined" style="margin-right: 10px; font-size: 1.2rem;">task_alt</i>
                Audit purchased
              </v-btn>

              <div v-else>
              <div v-if="!(!whiteGloveMissionControlAdminUi && site.stripe_hosted_invoice_url)" class="mb-6">
                <div v-if="contentAuditPackages" class="package-row">

                  <div
                    :class="{
                      'package-option': true,
                      'package-option-active': selectedPackage === 'WHITE_GLOVE_ADDON_CONTENT_AUDIT'
                    }"
                    :style="`${['user_editing'].includes(activeAudit?.order_status) ? null: 'opacity: 0.5; cursor: default;'}`"
                    @click="['user_editing'].includes(activeAudit?.order_status) ? updateAudit({is_speedy_delivery_order: 0}) : null"
                  >
                    <div class="package-price">${{ contentAuditPackages['WHITE_GLOVE_ADDON_CONTENT_AUDIT'].price }}</div>
                    <div class="package-name">Standard delivery</div>
                    <div class="package-desc">Content audit delivered in 5 business days</div>
                    <div class="package-note">Best if you are planning ahead</div>
                  </div>

                  <div
                    :class="{
                      'package-option': true,
                      'package-option-active': selectedPackage === 'WHITE_GLOVE_ADDON_CONTENT_AUDIT_SPEEDY_DELIVERY',
                      'package-option-popular': true
                    }"
                    :style="`${['user_editing'].includes(activeAudit?.order_status) ? null: 'opacity: 0.5; cursor: default;'}`"
                    @click="['user_editing'].includes(activeAudit?.order_status) ? updateAudit({is_speedy_delivery_order: 1}) : null"
                  >
                    <span class="popular-badge">Popular</span>
                    <div class="package-price">${{ contentAuditPackages['WHITE_GLOVE_ADDON_CONTENT_AUDIT_SPEEDY_DELIVERY'].price }}</div>
                    <div class="package-name">Speedy delivery</div>
                    <div class="package-desc">Content audit delivered in 72 hours</div>
                    <div class="package-note">Best if you need a result fast</div>
                  </div>

                </div>
              </div>

              <div class="included-block mb-6">
                <div class="font-weight-bold mainColor--text mb-4">What’s included</div>
                <ul class="included-list mainColor--text">
                  <li><i class="material-icons check-icon">check</i> Full website content &amp; EEAT analysis</li>
                  <li><i class="material-icons check-icon">check</i> Content plans with topic clusters</li>
                  <li><i class="material-icons check-icon">check</i> Main keywords + supporting topics for each cluster</li>
                  <li><i class="material-icons check-icon">check</i> Recommended content strategy and priorities</li>
                  <li><i class="material-icons check-icon">check</i> Project plan with actionable next steps</li>
                </ul>
              </div>

              <div
                style="display: flex; flex-direction: row; align-items: center; flex-wrap: wrap; gap: 12px;"
              >
                <template v-if="['in_checkout'].includes(activeAudit?.order_status)">
                  <div>
                    <v-btn
                      rounded
                      outlined
                      color="profileAnchorColor"
                      class="text-none"
                      @click="updateAudit({set_user_editing: true})"
                    >
                      <span>Cancel checkout session</span>
                    </v-btn>
                  </div>
                </template>
                <template v-else-if="['in_cart'].includes(activeAudit?.order_status)">
                  <div>
                    <v-btn
                      rounded
                      outlined
                      color="profileAnchorColor"
                      class="text-none"
                      @click="updateAudit({set_user_editing: true})"
                    >
                      <span>Remove from cart</span>
                    </v-btn>
                  </div>
                </template>
                <template v-else-if="['in_cart_checkout'].includes(activeAudit?.order_status)">
                  In cart checkout session
                </template>

                <template v-else>
                  <v-btn
                    v-if="whiteGloveMissionControlAdminUi"
                    rounded
                    color="profileAnchorColor"
                    class="button-with-icon text-none whiteColor--text padding8 height38"
                    tag="a"
                    @click="showSendInvoiceConfirmModal = true"
                  >
                    <i class="material-icons-outlined" style="margin-right: 10px; font-size: 1.2rem;">shopping_basket</i>
                    Send invoice to user (${{ finalPrice }})
                  </v-btn>
                  <template v-else>
                    <v-btn
                      v-if="site.stripe_hosted_invoice_url"
                      rounded
                      color="profileAnchorColor"
                      class="button-with-icon text-none whiteColor--text padding8 height38"
                      tag="a"
                      :href="site.stripe_hosted_invoice_url"
                      target="_blank"
                    >
                      <i class="material-icons-outlined" style="margin-right: 10px; font-size: 1.2rem;">shopping_basket</i>
                      Pay your invoice (${{ site.stripe_hosted_invoice_amount }})
                    </v-btn>
                    <v-btn
                      v-else
                      rounded
                      color="profileAnchorColor"
                      class="button-with-icon text-none whiteColor--text padding8 height38"
                      tag="a"
                      @click="buyNow();"
                    >
                      Buy {{ selectedPackage === 'WHITE_GLOVE_ADDON_CONTENT_AUDIT_SPEEDY_DELIVERY' ? 'speedy' : 'standard' }} delivery ${{ finalPrice }}
                      <span style="margin-left: 6px;">&rarr;</span>
                    </v-btn>
                  </template>
  
                  <v-btn
                    v-if="whiteGloveMissionControlAdminUi"
                    rounded
                    color="profileAnchorColor"
                    class="button-with-icon text-none whiteColor--text padding8 height38"
                    @click="updateAudit({set_in_cart: true})"
                  >
                    <span>Add to cart</span>
                  </v-btn>
  
                  <template v-if="!(!whiteGloveMissionControlAdminUi && site.stripe_hosted_invoice_url)">
                    <span
                      v-if="appliedCoupon"
                      style="color: #0CB370;"
                    ><i class="material-icons" style="vertical-align: sub;">check</i> Coupon applied ({{ appliedCoupon.coupon.couponType === 'Amount Off' ? '$':'' }}{{ appliedCoupon.coupon.amountOff }}{{ appliedCoupon.coupon.couponType === 'Percent Off' ? '%':'' }} off)</span>
                    <a
                      v-else
                      href="#" @click.prevent="showCouponModal = true"
                      style="color: var(--v-link15Color-base); font-weight: 600;"
                    >Apply a coupon</a>
                  </template>
  
                  <b-dropdown
                    v-if="whiteGloveMissionControlAdminUi"
                    aria-role="list" @click.native.stop
                    class="sales-tag-dropdown"
                  >
                    <template #trigger>
                      <v-btn
                        style="min-width: 110px;"
                        class="sales-tag-btn"
                        small
                        :color="activeAudit?.sales_tag === 'active' ? 'success' : undefined"
                      >
                        <template v-if="activeAudit?.sales_tag === 'passive'">Passive sale</template>
                        <template v-else-if="activeAudit?.sales_tag === 'active'">Active sale</template>
                        <template v-else-if="!activeAudit?.sales_tag">Passive sale</template>
                        <template v-else>{{ activeAudit?.sales_tag }}</template>
                      </v-btn>
                    </template>
                    <b-dropdown-item :disabled="activeAudit?.sales_tag === 'passive'" aria-role="listitem" @click.native.stop="updateAudit({sales_tag: 'passive'})">Set as passive sale</b-dropdown-item>
                    <b-dropdown-item :disabled="activeAudit?.sales_tag === 'active'" aria-role="listitem" @click.native.stop="updateAudit({sales_tag: 'active'})">Set as <b>active sale</b></b-dropdown-item>
                  </b-dropdown>
                </template>

                <div class="modal" :class="{'is-active': showCouponModal}">
                  <div class="modal-background" @click.stop="showCouponModal = false"></div>
                  <div class="modal-content">
                    <div class="box modal-container">
                      <div class="modal-content-root">
                        <p class="title-font is-size-4 mainColor--text">Apply coupon</p>
                        <div class="mainColor--text" style="font-size: 1rem; line-height: 150%;">
                          Insert your coupon here for a discount<br /><br />
                          <v-text-field outlined dense
                            v-model="modalCouponFieldValue"
                            placeholder="Enter coupon"
                            color="grayColor"
                          ></v-text-field>
                        </div>
                        <div class="d-flex justify-end gap10 mt-5">
                          <v-btn rounded outlined color="mainColor"
                            class="text-none"
                            @click.stop="() => {
                              showCouponModal = false;
                            }">
                            <span>Cancel</span>
                          </v-btn>
                          <v-btn rounded color="profileAnchorColor"
                            class="whiteColor--text text-none"
                            :disabled="!modalCouponFieldValue"
                            @click.stop="() => {
                              applyCoupon();
                            }"
                          >
                            <span>Apply coupon</span>
                          </v-btn>
                          
                        </div>
                      </div>
                    </div>
                    <button
                      class="custom-modal-close-btn"
                      aria-label="close"
                      @click="() => (showCouponModal = false)">
                      <i class="material-icons">close</i>
                    </button>
                  </div>
                </div>

              </div>
              </div>
            </div>
          </div>
    
          <div
            v-if="activeAudit && currentStep === 2"
            :class="{
              'step-content': true,
              'completed': isStepValid(currentStep)
            }"
          >
            <div
              v-if="contentAuditPackages"
              class="plan-summary-bar d-flex align-center justify-space-between flex-wrap mb-6"
            >
              <div class="d-flex align-center gap2">
                <i class="material-icons-outlined plan-summary-check">check</i>
                <span class="mainColor--text font-weight-medium" style="font-size: 1rem;">
                  <template v-if="selectedPackage === 'WHITE_GLOVE_ADDON_CONTENT_AUDIT_SPEEDY_DELIVERY'">
                    <span style="font-size: 1rem; font-weight: 700;">Speedy plan</span> — ${{ contentAuditPackages['WHITE_GLOVE_ADDON_CONTENT_AUDIT_SPEEDY_DELIVERY'].price }}, delivered in 72 hours
                  </template>
                  <template v-else>
                    <span style="font-size: 1rem; font-weight: 700;">Standard plan</span> — ${{ contentAuditPackages['WHITE_GLOVE_ADDON_CONTENT_AUDIT'].price }}, delivered in 5 business days
                  </template>
                </span>
              </div>
              <a
                v-if="!isStepValid(1)"
                href="#"
                class="change-plan-link"
                @click.prevent="currentStep = 1"
              >Change plan</a>
            </div>

            <form-step
              v-if="activeAudit"
              ref="formStep"
              :site="site"
              :active-audit="activeAudit"
              @audit-updated="$emit('auditUpdated', $event)"
              @readiness-change="bumpFooterReadiness"
            />
          </div>
    
          <div
            v-if="activeAudit && currentStep === 3"
            class="step-content"
          >
            <google-data-step
              v-if="activeAudit"
              ref="googleDataStep"
              :site="site"
              :active-audit="activeAudit"
              @success="onGoogleDataSuccess"
              @readiness-change="bumpFooterReadiness"
              @submitting-change="gscSubmitting = $event"
            />
          </div>

          <div
            v-if="currentStep <= STEPS.length && !(currentStep === 1 && !isStepValid(1))"
            class="wizard-footer d-flex align-center flex-wrap mt-4"
            style="gap: 12px;"
          >
            <v-btn
              v-if="currentStep > 1"
              rounded
              outlined
              color="profileAnchorColor"
              class="text-none height38"
              @click="currentStep -= 1"
            >
              &larr; Back
            </v-btn>
            <v-spacer />
            <v-btn
              v-if="currentStep === 1 && canContinueFromStep1"
              rounded
              color="profileAnchorColor"
              class="text-none whiteColor--text height38"
              :disabled="!canContinueFromStep1"
              @click="currentStep = 2"
            >
              Continue &rarr;
            </v-btn>
            <v-btn
              v-if="currentStep === 2"
              rounded
              color="profileAnchorColor"
              class="text-none whiteColor--text height38"
              :disabled="!canContinueFromStep2 || step2SubmitLoading"
              :loading="step2SubmitLoading"
              @click="handleStep2Continue"
            >
              Continue &rarr;
            </v-btn>
            <v-btn
              v-if="currentStep === 3"
              rounded
              color="profileAnchorColor"
              class="text-none whiteColor--text height38"
              :disabled="!canCompleteGscSetup || gscSubmitting"
              :loading="gscSubmitting"
              @click="handleStep3Complete"
            >
              Complete setup
            </v-btn>
          </div>

        </div>

        <div
          v-if="showSendInvoiceConfirmModal && whiteGloveMissionControlAdminUi"
          class="modal"
          :class="{
            'is-active': true,
          }"
        >
          <div
            class="modal-background"
            @click="
              showSendInvoiceConfirmModal = false
            "
          ></div>
          <div class="modal-content">
            <div class="box modal-container">
              <div class="pad25">
                <div>
                  <div
                    class="mainColor--text font-weight-bold mb-5"
                    style="font-size: 20px"
                  >
                    Are you sure?
                  </div>
                  <div
                    class="mainColor--text mb-5"
                    style="font-size: 16px; line-height: 24px"
                  >
                    You are about to send an invoice by email to the site owner's billing email (<b style="font-size: 16px; line-height: 24px">{{ selectedSubAccountRelation?.agencyInvoicingEmail || selectedSubAccountRelation?.agencyUserEmail }}</b>) for purchasing a Site Content Audit.
                    <br /><br />
                    Are you sure you want to proceed?
                  </div>
                </div>
                <div class="text-right">
                  <v-btn
                    rounded
                    color="profileAnchorColor"
                    class="button-with-icon whiteColor--text text-none ml-2"
                    @click="buyNow('invoice')"
                  >
                    <span>Yes, send invoice (${{ finalPrice }})</span>
                  </v-btn>
                  <v-btn
                    rounded
                    outlined
                    color="mainColor"
                    class="button-with-icon mainColor--text text-none ml-2"
                    @click="
                      () => {
                        showSendInvoiceConfirmModal = false;
                      }
                    "
                  >
                    <span>Cancel</span>
                  </v-btn>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div
          v-if="showForceOnboardingCompletedConfirmModal && whiteGloveMissionControlAdminUi"
          class="modal"
          :class="{
            'is-active': true,
          }"
        >
          <div
            class="modal-background"
            @click="
              showForceOnboardingCompletedConfirmModal = false
            "
          ></div>
          <div class="modal-content">
            <div class="box modal-container">
              <div class="pad25">
                <div>
                  <div
                    class="mainColor--text font-weight-bold mb-5"
                    style="font-size: 20px"
                  >
                    Are you sure?
                  </div>
                  <div
                    class="mainColor--text mb-5"
                    style="font-size: 16px; line-height: 24px"
                  >
                    This will set all onboarding steps as completed and update Asana onboarding tasks.
                  </div>
                </div>
                <div class="text-right">
                  <v-btn
                    rounded
                    color="profileAnchorColor"
                    class="button-with-icon whiteColor--text text-none ml-2"
                    @click="$store.commit('showLoading'); updateAudit({force_onboarding_completed: 1}).finally(() => {$store.commit('hideLoading'); showForceOnboardingCompletedConfirmModal = false})"
                  >
                    <span>Yes, proceed</span>
                  </v-btn>
                  <v-btn
                    rounded
                    outlined
                    color="mainColor"
                    class="button-with-icon mainColor--text text-none ml-2"
                    @click="
                      () => {
                        showForceOnboardingCompletedConfirmModal = false;
                      }
                    "
                  >
                    <span>Cancel</span>
                  </v-btn>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div
          v-if="showSetPaidConfirmModal && whiteGloveMissionControlAdminUi"
          class="modal"
          :class="{
            'is-active': true,
          }"
        >
          <div
            class="modal-background"
            @click="
              showSetPaidConfirmModal = false
            "
          ></div>
          <div class="modal-content">
            <div class="box modal-container">
              <div class="pad25">
                <div>
                  <div
                    class="mainColor--text font-weight-bold mb-5"
                    style="font-size: 20px"
                  >
                    Are you sure?
                  </div>
                  <div
                    class="mainColor--text mb-5"
                    style="font-size: 16px; line-height: 24px"
                  >
                    This will set mark the audit as paid and create Asana tasks.
                  </div>
                </div>
                <div class="text-right">
                  <v-btn
                    rounded
                    color="profileAnchorColor"
                    class="button-with-icon whiteColor--text text-none ml-2"
                    @click="$store.commit('showLoading'); updateAudit({set_paid: 1}).finally(() => {$store.commit('hideLoading'); showSetPaidConfirmModal = false})"
                  >
                    <span>Yes, proceed</span>
                  </v-btn>
                  <v-btn
                    rounded
                    outlined
                    color="mainColor"
                    class="button-with-icon mainColor--text text-none ml-2"
                    @click="
                      () => {
                        showSetPaidConfirmModal = false;
                      }
                    "
                  >
                    <span>Cancel</span>
                  </v-btn>
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
import FormStep from './FormStep.vue'
import GoogleDataStep from './GoogleDataStep.vue'
import { getFulfillementStatusLabel } from './utils'


const STEPS = [
  {
    label: 'Choose plan'
  },
  {
    label: 'Tell us about your site'
  },
  {
    label: 'Connect Google'
  }
]

export default {
  components: { FormStep, GoogleDataStep },
  props: [
    'site',
    'loadData',
    'activeAudit',
    'contentAuditPackages',
    'showOrderNewAuditButton',
  ],
  data () {
    return {
      currentStep: 1,
      stepsCount: 3,
      footerReadinessTick: 0,
      step2SubmitLoading: false,
      gscSubmitting: false,
      showCouponModal: false,
      modalCouponFieldValue: '',
      appliedCoupon: null,
      newAuditOrderLoading: false,
      showSendInvoiceConfirmModal: false,
      showForceOnboardingCompletedConfirmModal: false,
      showSetPaidConfirmModal: false,
    }
  },
  computed: {
    STEPS: () => STEPS,
    user () {
      if (this.$store.state.user) {
        return this.$store.state.user
      }
      return null
    },
    selectedSubAccountRelation () {
      return this.$store.state.selectedSubAccountRelation
    },
    isMissionControlSession () {
      return Boolean(this.$store.state.selectedSubAccountRelation?.isMissionControlSubAccount)
    },
    isWhiteGloveAdminMode () {
      return this.$store.state.isWhiteGloveAdminMode
    },
    whiteGloveMissionControlAdminUi () {
      return this.isMissionControlSession && this.isWhiteGloveAdminMode
    },
    finalPrice() {
      const pkg = this.contentAuditPackages?.[this.selectedPackage]
      if (!pkg) {
        return 0
      }
      let price = pkg.price
      let amountOff = 0
      if (this.appliedCoupon) {
        amountOff = this.appliedCoupon.coupon.amountOff
        if (this.appliedCoupon.coupon.couponType == 'Percent Off') {
          amountOff = (price * amountOff / 100)
        }
      }
      return (price - amountOff)
    },
    selectedPackage() {
      if (this?.activeAudit?.isSpeedyDeliveryOrder) {
        return 'WHITE_GLOVE_ADDON_CONTENT_AUDIT_SPEEDY_DELIVERY'
      }
      return 'WHITE_GLOVE_ADDON_CONTENT_AUDIT'
    },
    canContinueFromStep1 () {
      return this.isStepValid(1)
    },
    canContinueFromStep2 () {
      void this.footerReadinessTick
      if (!this.isStepValid(1)) {
        return false
      }
      if (this.activeAudit?.status === 'form_submitted') {
        return true
      }
      const fs = this.$refs.formStep
      return Boolean(fs && fs.isFormValid)
    },
    canCompleteGscSetup () {
      void this.footerReadinessTick
      if (!this.isStepValid(1) || !this.isStepValid(2)) {
        return false
      }
      const g = this.$refs.googleDataStep
      return Boolean(g && g.canProceed)
    },
  },
  watch: {
    selectedPackage: function (newVal, oldVal) {
      if (newVal !== oldVal) {
        this.appliedCoupon = null
      }
    },
    currentStep () {
      this.$nextTick(() => {
        this.bumpFooterReadiness()
      })
    },
  },
  methods: {
    getFulfillementStatusLabel: getFulfillementStatusLabel,
    isStepValid(step) {
      if (step === 1) {
        return this.activeAudit?.order_status === 'paid'
      }
      if (step === 2) {
        return this.isStepValid(step - 1) && this?.activeAudit?.status === 'form_submitted'
      }
      if (step === 3) {
        return (
          this.isStepValid(step - 1) &&
          (Boolean(this?.activeAudit?.hasAssociatedDomainLevelCampaign) || Boolean(this?.activeAudit?.has_ignored_associated_campaign_requirement)) &&
          Boolean(this?.activeAudit?.hasRetrievedFreshGSCData)
        )
      }
      return false
    },
    bumpFooterReadiness () {
      this.footerReadinessTick += 1
    },
    async handleStep2Continue () {
      if (this.activeAudit?.status === 'form_submitted') {
        this.currentStep = 3
        this.$nextTick(() => {
          this.bumpFooterReadiness()
          if (this.$refs.googleDataStep && this.$refs.googleDataStep.loadDomainLevelCampaign) {
            this.$refs.googleDataStep.loadDomainLevelCampaign()
          }
        })
        return
      }
      this.step2SubmitLoading = true
      try {
        const ok = await this.$refs.formStep.submitForm()
        if (ok) {
          await this.loadData()
          this.currentStep = 3
          this.$nextTick(() => {
            this.bumpFooterReadiness()
            if (this.$refs.googleDataStep && this.$refs.googleDataStep.loadDomainLevelCampaign) {
              this.$refs.googleDataStep.loadDomainLevelCampaign()
            }
          })
        }
      } finally {
        this.step2SubmitLoading = false
      }
    },
    handleStep3Complete () {
      if (this.$refs.googleDataStep) {
        this.$refs.googleDataStep.onStart()
      }
    },
    onGoogleDataSuccess () {
      this.gscSubmitting = false
      this.loadData()
        .then(() => this.refreshCurrentStep())
        .then(() => {
          this.$store.commit('triggerRefreshKeywordResearchCard')
        })
    },
    applyCoupon () {
      this.appliedCoupon = null;
      this.$store.commit('showLoading')
      return this.$store.dispatch('fetchValidatedCoupons', { promoCode: this.modalCouponFieldValue.trim(), isRegisterPage: 0 })
        .then((response) => {
          if (!response.data) {
            this.$notify({
              group: 'info', type: 'warning',
              text: 'Invalid coupon'
            })
            return;
          }
          if (response.data && response.data.status == 'SUCCESS') {
            let allServiceCoupons = response.data.coupons
            if (allServiceCoupons.length == 1) {
              const coupon = allServiceCoupons[0];
              if (['Percent Off', 'Amount Off'].includes(coupon.couponType) && coupon.product === this.selectedPackage) {
                this.appliedCoupon = {
                  code: this.modalCouponFieldValue.trim(),
                  coupon
                };
                this.showCouponModal = false;
              } else {
                this.$notify({
                  group: 'info', type: 'warning',
                  text: 'Invalid coupon'
                })
              }
            } else {
              this.$notify({
                group: 'info', type: 'warning',
                text: 'Invalid coupon'
              })
            }
          } else {
            if (response.data.status == 'FAILURE') {
              this.$notify({
                group: 'info', type: 'warning',
                text: response.data.errorMsg ? response.data.errorMsg : 'Invalid coupon'
              })
            }
          }
        })
        .finally(() => {
          this.$store.commit('hideLoading')
        })
    },
    buyNow(mode='checkout') {
      this.$store.commit('showLoading');
      this.$store.dispatch('createStripePayment', {
        packageType: 'WHITE_GLOVE_ADDON_PURCHASE',
        userID: this?.selectedSubAccountRelation?.ownerId || this.user.id,
        items: [
          {
            'type': 'audit_order',
            'id': this.activeAudit.id
          }
        ],
        redirect_front_url: this.$router.currentRoute.fullPath,
        redirect_front_for_cancel_url: this.$router.currentRoute.fullPath,
        promoCode: this.appliedCoupon?.code,
        mode,
      })
      .then((response) => {
        if (response.data.url) {
          window.open(response.data.url, '_self')
        } else if (mode === 'invoice' && response.data.status === 'SUCCESS') {
          this.$notify({
            group: 'info', type: 'success',
            text: 'Invoice sent to user'
          })
        }
      })
      .finally(() => {
        this.$store.commit('hideLoading');
      })
    },
    updateAudit(payload) {
      return this.$store.dispatch('updateSiteContentAudit', {
        auditId: this.activeAudit.id,
        body: payload
      })
        .then(response => {
          if (response.data && response.data.status == 'SUCCESS') {
            if (payload.set_in_cart || payload.set_user_editing) {
              this.$store.commit('triggerRefreshSiloContentReportsOrdersCart', true);
            }
            return this.loadData().then(() => this.refreshCurrentStep())
          }
        })
    },
    updateSite(payload) {
      return this.$store.dispatch('updateSite', {
        siteId: this.site.id,
        body: payload
      })
        .then(response => {
          if (response.data && response.data.status == 'SUCCESS') {
            return this.loadData().then(() => this.refreshCurrentStep()).then(() => this.loadSites())
          }
        })
    },
    loadSites() {
      return this.$store
        .dispatch("loadSites")
        .then(response => {
          if (response.data && response.data.status === "SUCCESS") {
            this.$store.commit("setSites", response.data.sites);
          }
        })
    },
    newAuditOrder() {
      this.newAuditOrderLoading = true;
      this.$store.dispatch('createSiteContentAudit', {
        body: {
          siteId: this.site.id,
        }
      })
        .then(response => {
          if (response.data && response.data.status == 'SUCCESS') {
            return this.loadData(true)
          }
        })
        .finally(() => {
          this.newAuditOrderLoading = false;
        })
    },
    refreshCurrentStep() {
      if (this.activeAudit) {
        // go to the last valid step or default to first step
        let currentStep = 1
        for (let index = 1; index <= this.stepsCount; index++) {
          if (this.isStepValid(index)) {
            currentStep = index + 1;
          } else {
            break;
          }
        }
        this.currentStep = currentStep;
      }
    },
  },
  mounted () {
    this.refreshCurrentStep()
  },
}
</script>

<style lang="scss" scoped>
.container-launch {

  display: flex;

  .audit-wizard-inner {
    width: 100%;
    max-width: 52rem;
  }

  .mc-corner-link {
    color: var(--v-link15Color-base);
    position: absolute;
    top: 0;
    right: 0;
    font-size: 0.85rem;
    z-index: 1;
  }

  .plan-card {
    background: #fff;
  }

  .package-row {
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    gap: 20px;

    @media (max-width: 600px) {
      flex-direction: column;
    }
  }

  .package-option-popular {
    position: relative;
    padding-top: 28px;
  }

  .popular-badge {
    position: absolute;
    top: 12px;
    right: 12px;
    font-size: 0.7rem;
    font-weight: 700;
    text-transform: uppercase;
    color: #ff5c00;
    letter-spacing: 0.04em;
    border: 1px solid #ff5c00;
    background-color: #FF57001A;
    border-radius: 6px;
    padding: 2px 10px;
  }

  .included-list {
    list-style: none;
    padding: 0;
    margin: 0;
    font-size: 0.95rem;
    line-height: 1.7;

    li {
      display: flex;
      align-items: flex-start;
      gap: 8px;
      margin-bottom: 12px;
    }

    .check-icon {
      color: #11A33D;
      font-size: 1.1rem;
      flex-shrink: 0;
      margin-top: 2px;
    }
  }

  .plan-summary-bar {
    padding: 18px 16px;
    border-radius: 8px;
    background: #EEFEF6;
    border: 1px solid #53FFA9;
  }

  .plan-summary-check {
    color: #11A33D;
    font-size: 22px;
  }

  .change-plan-link {
    color: var(--v-link15Color-base);
    font-weight: 600;
    font-size: 0.9rem;
  }

  .gap2 {
    gap: 8px;
  }

  .v-btn.v-btn--disabled.success-disabled-btn {
    background-color: #0CB370 !important;
    color: #fff !important;

    .material-icons-outlined {
      font-size: 1.2rem;
    }
  }

  .onboarding-completed {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 50px;

    img {
      height: 160px;
      margin-bottom: -50px;
    }
  }

  .step-control {
    padding-top: 110px;
    text-align: center;

    .v-btn {
      background-color: #fff;
      border-color: #CCCCCC;
      border-radius: 6px;
      box-shadow: 0px 0.32px 7.93px 0px rgba(0, 0, 0, 0.11);

      &.v-btn--disabled {
        opacity: 0.4 !important;
      }
    }
  }

  .steps-status {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    align-items: center;
    gap: 0;

    .step-item {
      display: flex;
      flex-direction: row;
      align-items: center;
      gap: 12px;

      &, * {
        cursor: default;
      }

      .step-dot-label-group {
        display: flex;
        flex-direction: row;
        align-items: center;
        gap: 12px;
      }

      .step-label {
        font-weight: 400;
        font-size: 0.9rem;
        white-space: nowrap;
        color: #8c8c8c;
        line-height: 1.25;
      }

      .step-dot {
        box-sizing: border-box;
        flex-shrink: 0;
        border-radius: 50%;
        border: none;
        background: #d9d9d9;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 38px;
        height: 38px;
        line-height: 1;

        .step-number {
          font-size: 1rem;
          font-weight: 700;
          color: #fff;
        }

        .material-icons {
          display: none;
          font-size: 1.35rem;
          font-weight: bold;
          line-height: 1;
        }
      }

      .step-seperator {
        flex-shrink: 0;
        width: 40px;
        height: 1px;
        margin-right: 16px;
        background: #d9d9d9;
        align-self: center;
      }

      &.active:not(.completed) {
        .step-label {
          font-weight: 700;
          color: #00424d;
        }

        .step-dot {
          background-color: #ff5a00;

          .step-number {
            color: #fff;
          }
        }
      }

      &.completed {
        .step-label {
          font-weight: 700;
          color: #11A33D;
        }

        .step-dot {
          background: #11A33D;

          .step-number {
            display: none;
          }

          .material-icons {
            display: block;
            color: #fff;
          }
        }

        .step-seperator {
          background: #11A33D;
        }
      }
    }
  }

  .step-content {
    &.show-border {
      border: 1px solid #ddd;
      border-radius: 12px;
      padding: 30px;
    }
    
    .v-btn {
      letter-spacing: initial;
    }
    
    .step-text {
      color: var(--v-primary-base);
    }

    .step-text-number {
      font-size: 0.95rem;
      font-weight: 800;
    }
  
    .step-text-title {
      font-size: 1.25rem;
      font-weight: 800;
    }
  
    .step-text-description {
      font-size: 0.9rem !important;
      font-weight: 400 !important;
      line-height: 1.6rem;
    }

    &.completed {
      .step-text {
        text-decoration: line-through;
      }
    }
  }

  .height38 {
    height: 2.375rem !important;
  }

  .padding8 {
    padding: 8px 12px !important;
  }

  .cross-container {
    margin-top: -10px;
  }

  .package-option {
    flex: 1;
    padding: 30px 20px;
    cursor: pointer;
    border-radius: 12px;
    border: 1px solid var(--v-grayColor-base);
    color: var(--v-mainColor-base);
    display: flex;
    flex-direction: column;
    align-items: flex-start;

    .package-price {
      font-size: 1.5rem;
      font-weight: 700;
      color: var(--v-mainColor-base);
      margin-bottom: 20px;
    }

    .package-name {
      font-size: 1rem;
      font-weight: 700;
      color: var(--v-mainColor-base);
      margin-bottom: 10px;
    }

    .package-desc {
      font-size: 0.9rem;
      color: #666;
      margin-bottom: 10px;
    }

    .package-note {
      font-size: 0.9rem;
      font-style: italic;
      color: #666;
    }

    &.package-option-active {
      border-color: var(--v-profileAnchorColor-base);
      background-color: #FFFFF9;
      border-width: 2px;
    }
  }
}

.sales-tag-dropdown {
  .dropdown-item {
    font-weight: normal;
  }

  &.is-disabled {
    opacity: initial;
    cursor: default;
  }
}

.sales-tag-btn {
  text-transform: initial;
  letter-spacing: initial;
}
</style>

<style lang="scss">

.container-launch .step-control .v-btn.v-btn--plain:not(.v-btn--active):not(.v-btn--disabled):not(.v-btn--loading):not(:focus):not(:hover) .v-btn__content {
  opacity: 1 !important;
}

</style>